/**
 * qrPrint — one shared code path for printing QR "service" slips, used by both
 * the single-table/session modals and the bulk-print modals so every QR slip
 * looks identical no matter where it was triggered.
 *
 * Two output paths, same slip renderer (renderQrSlipCanvas):
 *  - WebUSB (desktop till): one raster slip per item, each cut individually
 *    right under the QR (no ~7cm CUPS driver tail).
 *  - PDF fallback (iOS/Safari/mobile, or a USB failure mid-job): one page per
 *    item with the SAME slip canvas embedded as an image, so the PDF matches
 *    the thermal print exactly.
 */

import QRCode from 'qrcode';
import { createReportPdf } from '@/utils/pdfReport';
import { isSupported, printCanvas, renderQrSlipCanvas, dotsForPaperWidth } from '@/utils/escposPrinter';
import { printerPaperWidth } from '@/utils/printerSettings';

// Customer self-service page a QR points at. Session token drives it, so the
// same builder works for a table session or a takeaway/delivery session.
export function serviceUrl(token) {
  return process.env.VUE_APP_URL + '/service/' + token;
}

/**
 * Print a batch of QR slips.
 * Paper width = this device's printer setting, read synchronously (a fetch here
 * would break WebUSB's user-gesture requirement).
 *
 * @param slips  [{ heading, subtitle, url }]
 * @param opts.onProgress      (n) => void, fired after each slip is sent
 * @returns { method: 'webusb'|'pdf'|'none', count }
 */
export async function printQrSlips(slips, { onProgress } = {}) {
  const list = (slips || []).filter(s => s && s.url);
  if (!list.length) return { method: 'none', count: 0 };
  const billPaperWidth = printerPaperWidth();
  const widthPx = dotsForPaperWidth(billPaperWidth);

  if (isSupported()) {
    try {
      for (let i = 0; i < list.length; i++) {
        const s = list[i];
        const qrDataUrl = await QRCode.toDataURL(s.url, { width: 384, margin: 1 });
        const canvas = await renderQrSlipCanvas({ heading: s.heading, subtitle: s.subtitle, qrDataUrl, widthPx });
        // feedLines:0 — the slip is tiny; GS V B already feeds to the cutter.
        await printCanvas(canvas, { feedLines: 0 });
        if (onProgress) onProgress(i + 1);
      }
      return { method: 'webusb', count: list.length };
    } catch (e) {
      // OS/CUPS holds the interface, unit unplugged, etc. → whole batch to PDF.
      console.warn('WebUSB QR print failed; falling back to PDF:', e);
    }
  }

  await printQrSlipsPdf(list, { widthPx, billPaperWidth });
  if (onProgress) onProgress(list.length);
  return { method: 'pdf', count: list.length };
}

async function printQrSlipsPdf(list, { widthPx, billPaperWidth }) {
  // Page width tracks the paper (72mm → 204.09pt, 58mm → 164.41pt) so the slip
  // fills the sheet the way the thermal print does; height auto-fits each slip.
  const pageWidthPt = billPaperWidth === '58mm' ? 164.41 : 204.09;
  const sideMargin = 8;
  const imgWidth = pageWidthPt - sideMargin * 2;

  const content = [];
  for (let i = 0; i < list.length; i++) {
    const s = list[i];
    const qrDataUrl = await QRCode.toDataURL(s.url, { width: 384, margin: 1 });
    const canvas = await renderQrSlipCanvas({ heading: s.heading, subtitle: s.subtitle, qrDataUrl, widthPx });
    content.push({
      image: canvas.toDataURL('image/png'),
      width: imgWidth,
      alignment: 'center',
      pageBreak: i === 0 ? undefined : 'before',
    });
  }

  const pdf = createReportPdf({
    pageSize: { width: pageWidthPt, height: 'auto' },
    pageMargins: [sideMargin, 12, sideMargin, 12],
    header: () => '',
    footer: () => '',
    content,
  });
  const blob = await pdf.getBlob();
  const url = URL.createObjectURL(blob);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobile) {
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    return;
  }

  let iframe = document.getElementById('__qr_print_frame');
  if (iframe) iframe.remove();
  iframe = document.createElement('iframe');
  iframe.id = '__qr_print_frame';
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
}
